import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageSquare, ExternalLink, Trash2, ShieldCheck, ShieldX } from "lucide-react";

const TYPE_CONFIG = {
  support_ticket: { label: "Support", className: "bg-slate-100 text-slate-600" },
  broadcast: { label: "Broadcast", className: "bg-blue-100 text-blue-700" },
  intra_org_share: { label: "Share", className: "bg-teal-100 text-teal-700" },
};
const CAT_CONFIG = {
  bug: { label: "Bug", className: "bg-red-100 text-red-700" },
  consult: { label: "Consult", className: "bg-teal-100 text-teal-700" },
  question: { label: "Question", className: "bg-blue-100 text-blue-700" },
  feedback: { label: "Feedback", className: "bg-slate-100 text-slate-600" },
};

export default function AdminCommunications() {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [sortBy, setSortBy] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  // Broadcast state
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [bcAudience, setBcAudience] = useState("all");
  const [bcOrgId, setBcOrgId] = useState("");
  const [bcUserId, setBcUserId] = useState("");
  const [bcContent, setBcContent] = useState("");
  const [bcExpires, setBcExpires] = useState("");
  const [bcEmail, setBcEmail] = useState(false);
  const [bcSending, setBcSending] = useState(false);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, limit: "50" });
      if (statusFilter) params.set("status_filter", statusFilter);
      const r = await api.get(`/admin/communications?${params}`);
      setMessages(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadMessages(); }, [sortBy, statusFilter]); // eslint-disable-line
  useEffect(() => {
    api.get("/admin/clients").then(r => setOrgs(r.data)).catch(() => {});
    api.get("/admin/pending-invites").then(r => setPendingInvites(r.data)).catch(() => {});
  }, [api]);

  const loadThread = async (id) => {
    setSelectedId(id);
    try { const r = await api.get(`/messages/${id}/thread`); setThread(r.data); } catch { toast.error("Failed"); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    setReplying(true);
    try { await api.post(`/messages/${selectedId}/reply`, { content: replyText }); setReplyText(""); loadThread(selectedId); toast.success("Reply sent"); } catch { toast.error("Failed"); }
    finally { setReplying(false); }
  };

  const handleResolve = async () => {
    if (!selectedId) return;
    await api.put(`/admin/communications/${selectedId}/resolve`);
    toast.success("Resolved");
    loadMessages();
    loadThread(selectedId);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedId) return;
    await api.put(`/admin/communications/${selectedId}/status?status=${newStatus}`);
    toast.success(`Status changed to ${newStatus}`);
    loadMessages();
    loadThread(selectedId);
  };

  const handleApproveInvite = async (inviteId) => {
    try {
      await api.put(`/admin/pending-invites/${inviteId}/approve`);
      toast.success("Invite approved — registration email sent");
      setPendingInvites(p => p.filter(i => i.id !== inviteId));
      loadMessages();
      if (selectedId) loadThread(selectedId);
    } catch { toast.error("Approval failed"); }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      await api.put(`/admin/pending-invites/${inviteId}/reject`);
      toast.success("Invite rejected");
      setPendingInvites(p => p.filter(i => i.id !== inviteId));
      loadMessages();
      if (selectedId) loadThread(selectedId);
    } catch { toast.error("Rejection failed"); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/communications/${id}`);
    toast.success("Archived");
    loadMessages();
    if (selectedId === id) { setSelectedId(null); setThread(null); }
  };

  const handleBroadcast = async () => {
    if (!bcContent.trim()) return;
    setBcSending(true);
    try {
      await api.post("/admin/broadcast", {
        content: bcContent, audience: bcAudience,
        target_org_id: bcAudience === "org" ? bcOrgId : null,
        target_user_id: bcAudience === "user" ? bcUserId : null,
        expires_at: bcExpires || null, send_email_alert: bcEmail
      });
      toast.success("Broadcast sent");
      setShowBroadcast(false);
      setBcContent(""); setBcAudience("all"); setBcEmail(false); setBcExpires("");
      loadMessages();
    } catch { toast.error("Failed"); }
    finally { setBcSending(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-communications">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-heading text-slate-900">Communications</h1><p className="text-sm text-slate-500 mt-1">Support tickets, broadcasts, and shared searches</p></div>
        <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
          <DialogTrigger asChild><Button className="bg-evergreen-950 hover:bg-evergreen-900 text-white font-accent" data-testid="broadcast-btn"><Send className="w-4 h-4 mr-1" /> Broadcast</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Send Broadcast</DialogTitle><DialogDescription>Send a message to users across the platform.</DialogDescription></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Audience</Label>
                <Select value={bcAudience} onValueChange={setBcAudience}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Users</SelectItem><SelectItem value="org">Specific Organization</SelectItem><SelectItem value="user">Specific User</SelectItem></SelectContent></Select></div>
              {bcAudience === "org" && <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Organization</Label>
                <Select value={bcOrgId} onValueChange={setBcOrgId}><SelectTrigger><SelectValue placeholder="Select org..." /></SelectTrigger>
                  <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>}
              <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Message</Label><Textarea value={bcContent} onChange={e => setBcContent(e.target.value)} className="min-h-[100px]" data-testid="broadcast-content" /></div>
              <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Expires (Optional)</Label><Input type="datetime-local" value={bcExpires} onChange={e => setBcExpires(e.target.value)} /></div>
              <div className="flex items-center gap-3"><Switch checked={bcEmail} onCheckedChange={setBcEmail} /><span className="text-sm text-slate-600">Send email alert</span></div>
              <Button onClick={handleBroadcast} disabled={bcSending} className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white" data-testid="send-broadcast">{bcSending ? "Sending..." : "Send Broadcast"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invite Approvals */}
      {pendingInvites.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="pending-invites">
          <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Pending Invite Approvals ({pendingInvites.length})</h3>
          {pendingInvites.map(inv => (
            <div key={inv.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between animate-slideUp" data-testid={`pending-invite-${inv.id}`}>
              <div>
                <p className="text-sm text-slate-900"><span className="font-medium">{inv.invited_by_name}</span> at <span className="font-medium">{inv.org_name}</span> wants to invite:</p>
                <p className="text-sm font-mono text-amber-800 font-medium mt-0.5">{inv.invited_email}</p>
                <p className="text-[10px] text-slate-400 mt-1">{new Date(inv.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`reject-invite-${inv.id}`}
                  onClick={async () => { await api.put(`/admin/pending-invites/${inv.id}/reject`); toast.success("Invite rejected"); setPendingInvites(p => p.filter(i => i.id !== inv.id)); }}>
                  Reject
                </Button>
                <Button size="sm" className="bg-evergreen-950 hover:bg-evergreen-900 text-white" data-testid={`approve-invite-${inv.id}`}
                  onClick={async () => { await api.put(`/admin/pending-invites/${inv.id}/approve`); toast.success("Invite approved — registration email sent"); setPendingInvites(p => p.filter(i => i.id !== inv.id)); }}>
                  Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="newest">Newest First</SelectItem><SelectItem value="oldest">Oldest First</SelectItem><SelectItem value="unresolved">Unresolved First</SelectItem><SelectItem value="consult">Consult First</SelectItem></SelectContent></Select>
        <Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}><SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="open">Open</SelectItem><SelectItem value="resolved">Resolved</SelectItem></SelectContent></Select>
      </div>

      <div className="flex gap-4 min-h-[600px]">
        {/* Left: Message List */}
        <div className="w-96 flex-shrink-0 space-y-2 overflow-y-auto">
          {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white rounded-lg border animate-pulse" />) :
            messages.map(m => (
              <button key={m.id} onClick={() => loadThread(m.id)} data-testid={`admin-msg-${m.id}`}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedId === m.id ? "border-lime-500 bg-lime-50/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <Badge className={`text-[10px] border-0 ${TYPE_CONFIG[m.type]?.className || "bg-slate-100 text-slate-600"}`}>{TYPE_CONFIG[m.type]?.label || m.type}</Badge>
                  {m.metadata?.ticket_category && <Badge className={`text-[10px] border-0 ${CAT_CONFIG[m.metadata.ticket_category]?.className || ""}`}>{CAT_CONFIG[m.metadata.ticket_category]?.label}</Badge>}
                  {m.status === "resolved" && <Badge className="bg-lime-100 text-lime-700 text-[10px] border-0">Resolved</Badge>}
                  {m.is_unread && <span className="w-2 h-2 bg-lime-500 rounded-full" />}
                </div>
                <p className="text-sm text-slate-900 font-medium">{m.sender_name} <span className="text-xs text-slate-400 font-normal">{m.org_name}</span></p>
                <p className="text-xs text-slate-500 line-clamp-1">{m.content?.slice(0, 100)}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-slate-400 font-mono">{new Date(m.created_at).toLocaleString()}</span>
                  {m.reply_count > 0 && <span className="text-[10px] text-slate-400">{m.reply_count} replies</span>}
                </div>
              </button>
            ))}
        </div>

        {/* Right: Thread */}
        <div className="flex-1 bg-white rounded-xl border overflow-hidden flex flex-col">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center"><p className="text-sm text-slate-400">Select a message</p></div>
          ) : (
            <>
              {/* Context bar */}
              {thread.root?.metadata?.page && (
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-xs text-slate-500">
                  Submitted from: <span className="font-medium text-slate-700">{thread.root.metadata.page}</span>
                  {thread.root.metadata.search_id && <span className="ml-2">Search: {thread.root.metadata.search_id.slice(0, 8)}</span>}
                </div>
              )}
              {thread.root?.status === "resolved" && (
                <div className="bg-lime-50 border-b border-lime-200 px-4 py-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-lime-600" /><span className="text-sm text-lime-700 font-accent">Resolved</span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {[thread.root, ...(thread.replies || [])].map(m => (
                  <div key={m.id} className={`${m.sender_role === "master_admin" ? "ml-8" : "mr-8"}`}>
                    <div className={`rounded-lg p-3 ${m.sender_role === "master_admin" ? "bg-evergreen-950 text-white" : "bg-slate-50 border border-slate-200"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-accent font-medium ${m.sender_role === "master_admin" ? "text-lime-300" : "text-slate-700"}`}>{m.sender_name}</span>
                        <span className={`text-[10px] font-mono ${m.sender_role === "master_admin" ? "text-slate-400" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      <p className={`text-sm leading-relaxed ${m.sender_role === "master_admin" ? "text-slate-200" : "text-slate-600"}`}>{m.content}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Actions */}
              <div className="border-t border-slate-200 p-3">
                {/* Invite approval inline action */}
                {thread.root?.metadata?.ticket_category === "invite_approval" && thread.root?.metadata?.invite_id && thread.root?.status === "open" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-center justify-between" data-testid="inline-invite-approval">
                    <div>
                      <p className="text-sm text-amber-900 font-medium">Invite pending approval</p>
                      <p className="text-xs text-amber-700 font-mono">{thread.root.metadata.invited_email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleRejectInvite(thread.root.metadata.invite_id)}>
                        <ShieldX className="w-3 h-3 mr-1" /> Reject
                      </Button>
                      <Button size="sm" className="bg-evergreen-950 hover:bg-evergreen-900 text-white" onClick={() => handleApproveInvite(thread.root.metadata.invite_id)}>
                        <ShieldCheck className="w-3 h-3 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mb-2 items-center">
                  <Select value={thread.root?.status || "open"} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(selectedId)} className="text-red-500"><Trash2 className="w-3 h-3 mr-1" /> Archive</Button>
                </div>
                <div className="flex gap-2">
                  <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..." className="text-sm min-h-[40px] flex-1" data-testid="admin-reply" />
                  <Button onClick={handleReply} disabled={replying || !replyText.trim()} className="bg-evergreen-950 hover:bg-evergreen-900 text-white self-end" data-testid="admin-reply-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
